import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react'
import { useAuth } from '../hooks/useAuth'
import { api } from '../lib/api'

// Provider icon pill
function ProviderBadge({ name }: { name: string }) {
  return (
    <span
      className="px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{
        backgroundColor: 'var(--tg-theme-secondary-bg-color)',
        color: 'var(--tg-theme-hint-color)',
      }}
    >
      {name}
    </span>
  )
}

export default function ConnectPage() {
  const wallet = useTonWallet()
  const [tonConnectUI] = useTonConnectUI()
  const { apiKey } = useAuth()
  const isLoading = false
  const error = null
  const navigate = useNavigate()
  const [modelCount, setModelCount] = useState<number | null>(null)

  // Redirect to home once connected
  useEffect(() => {
    if (wallet && apiKey) {
      navigate('/', { replace: true })
    }
  }, [wallet, apiKey, navigate])

  // Fetch real model count
  useEffect(() => {
    api.listModels().then((m) => setModelCount(m.length)).catch(() => {})
  }, [])

  return (
    <div
      className="flex flex-col flex-1 px-6"
      style={{
        minHeight: 'var(--tg-viewport-stable-height, 100dvh)',
        backgroundColor: 'var(--tg-theme-bg-color)',
        paddingTop: 'max(80px, 15vh)',
        paddingBottom: 'max(32px, var(--safe-bottom))',
      }}
    >
      {/* Logo + title */}
      <div className="flex flex-col items-center gap-3 mb-10">
        <div
          className="w-[80px] h-[80px] rounded-[24px] flex items-center justify-center text-[36px] shadow-lg"
          style={{
            background: 'linear-gradient(135deg, var(--tg-theme-button-color) 0%, color-mix(in srgb, var(--tg-theme-button-color) 70%, #000) 100%)',
            color: 'var(--tg-theme-button-text-color)',
            boxShadow: '0 8px 32px color-mix(in srgb, var(--tg-theme-button-color) 40%, transparent)',
          }}
        >
          ✦
        </div>
        <h1
          className="text-[32px] font-bold tracking-tight"
          style={{ color: 'var(--tg-theme-text-color)' }}
        >
          Imago
        </h1>
        <p
          className="text-[15px] text-center leading-snug max-w-[260px]"
          style={{ color: 'var(--tg-theme-hint-color)' }}
        >
          AI marketplace on TON.{'\n'}Access {modelCount ? `${modelCount}+` : '30+'} models, pay with crypto.
        </p>

        {/* Provider logos row */}
        <div className="flex flex-wrap items-center justify-center gap-1.5 mt-1">
          {['GPT-5', 'Claude', 'Gemini', 'Grok', 'DeepSeek', '+more'].map((p) => (
            <ProviderBadge key={p} name={p} />
          ))}
        </div>
      </div>

      {/* Feature list */}
      <div className="flex flex-col gap-3 mb-4 w-full max-w-[280px] mx-auto">
        {[
          { icon: '⚡', title: `${modelCount ? `${modelCount}+` : '30+'} AI models`, desc: 'GPT-5, Claude, Gemini, DeepSeek and more' },
          { icon: '💳', title: 'Pay with TON', desc: 'No credit card, no KYC, no subscription' },
          { icon: '🔑', title: 'OpenAI-compatible API', desc: 'Drop-in replacement — one line to integrate' },
        ].map(({ icon, title, desc }) => (
          <div key={title} className="flex items-start gap-3">
            <span className="text-[20px] shrink-0 mt-0.5">{icon}</span>
            <div>
              <p
                className="text-[14px] font-semibold leading-tight"
                style={{ color: 'var(--tg-theme-text-color)' }}
              >
                {title}
              </p>
              <p
                className="text-[12px] mt-0.5"
                style={{ color: 'var(--tg-theme-hint-color)' }}
              >
                {desc}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="flex flex-col gap-3 mt-auto">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 h-14">
            <span
              className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"
              style={{ color: 'var(--tg-theme-button-color)' }}
            />
            <span className="text-sm" style={{ color: 'var(--tg-theme-hint-color)' }}>
              Connecting…
            </span>
          </div>
        ) : (
          <button
            onClick={() => tonConnectUI.openModal()}
            className="w-full h-[56px] rounded-2xl flex items-center justify-center gap-2 text-[16px] font-semibold active:opacity-80 transition-opacity"
            style={{
              backgroundColor: 'var(--tg-theme-button-color)',
              color: 'var(--tg-theme-button-text-color)',
            }}
          >
            {/* TON diamond icon */}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L17 7.5V12.5L10 18L3 12.5V7.5L10 2Z" fill="currentColor" fillOpacity="0.9" />
              <path d="M10 2L17 7.5H3L10 2Z" fill="currentColor" />
            </svg>
            Connect Wallet
          </button>
        )}

        {error && (
          <p
            className="text-[13px] text-center"
            style={{ color: 'var(--tg-theme-destructive-text-color)' }}
          >
            {error}
          </p>
        )}

        <p
          className="text-[12px] text-center"
          style={{ color: 'var(--tg-theme-hint-color)' }}
        >
          No KYC · No subscription · Pay per use
        </p>

        <button
          onClick={() => navigate('/', { replace: true })}
          className="text-[14px] font-medium active:opacity-70 transition-opacity mt-1"
          style={{ color: 'var(--tg-theme-accent-text-color)' }}
        >
          Browse without wallet →
        </button>
      </div>
    </div>
  )
}
